import { Icon, theme, Text } from 'components';
import { useDebounce } from 'hooks';
import React, { InputHTMLAttributes, useEffect, useState } from 'react';
import styled from 'styled-components';

interface ISearchInputBase {
  onSearch: (query?: string) => void;
  defaultValue?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
}

const DEBOUNCE_TIME_MS = 800;
const LIMIT_CHARACTERS = 80;

const SearchInputBase = ({
  onSearch,
  defaultValue = '',
  inputProps,
}: ISearchInputBase) => {
  const [searchQuery, setSearchQuery] = useState<string>(defaultValue);
  const debouncedValue = useDebounce(searchQuery, DEBOUNCE_TIME_MS);

  useEffect(() => {
    onSearch(debouncedValue);
  }, [debouncedValue, onSearch]);

  useEffect(() => {
    setSearchQuery(defaultValue);
  }, [defaultValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.slice(0, LIMIT_CHARACTERS);
    setSearchQuery(value);
  };

  return (
    <SearchHeader>
      <SearchInputWrapper>
        <Icon type="search" width={20} />
        <SearchInput
          placeholder="Search"
          onChange={handleChange}
          value={searchQuery}
          {...inputProps}
        />
        {searchQuery ? (
          <ClearButton onClick={() => setSearchQuery('')}>
            <Icon type="antdCloseCircleFilled" width={18} />
          </ClearButton>
        ) : null}
      </SearchInputWrapper>
      {searchQuery?.length >= LIMIT_CHARACTERS && (
        <Text type="secondary" fontSize={12} style={{ marginLeft: 5 }}>
          Max 80 characters
        </Text>
      )}
    </SearchHeader>
  );
};

export default SearchInputBase;

const ClearButton = styled.button`
  margin-left: 8px;
  display: flex;
`;

const SearchHeader = styled.div`
  border-bottom-width: 1px;
  border-bottom-color: #e9ecef;
`;

const SearchInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: #fff;
  border-radius: 8px;
  padding: 6px 8px;
  border: solid 1px #c5c6d0;
  gap: 8px;
  &:focus-within {
    border-color: ${theme.getColor('primary')};
  }
`;

const SearchInput = styled.input`
  flex: 1;
  font-size: 16px;
  color: #333;
  padding: 0;
  border: none;
  background-color: transparent;
  &:focus {
    outline: none;
  }
`;
